import { useCallback, useRef, useState } from "react";
import type { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";

import type { TranslatedViolations, UnmappedViolation } from "./serverViolations";

interface UseApplyServerFieldErrorsResult<T extends FieldValues> {
  /** Unmapped violations from the last application, for the section's banner. */
  unmapped: UnmappedViolation[];
  /** Replaces the previously applied server errors with these. */
  apply: (translated: TranslatedViolations<FieldPath<T>>) => void;
}

/**
 * Applies translated BFF violations as RHF `server` errors. Each call clears only the paths the previous
 * call set, so GET and PUT sources can share it and whichever fires last wins. Repeated violations on
 * one path are joined with a newline.
 * @template T
 * @param {UseFormReturn<T>} form - The section's form.
 * @returns {UseApplyServerFieldErrorsResult<T>} The unmapped violations and the applier.
 */
export function useApplyServerFieldErrors<T extends FieldValues>(
  form: UseFormReturn<T>
): UseApplyServerFieldErrorsResult<T> {
  const { setError, clearErrors } = form;
  const appliedPaths = useRef<Array<FieldPath<T>>>([]);
  const [unmapped, setUnmapped] = useState<UnmappedViolation[]>([]);

  const apply = useCallback(
    ({ fieldErrors, unmapped: nextUnmapped }: TranslatedViolations<FieldPath<T>>) => {
      clearErrors(appliedPaths.current);
      const messages = new Map<FieldPath<T>, string[]>();
      for (const { rhfPath, message } of fieldErrors) {
        messages.set(rhfPath, [...(messages.get(rhfPath) ?? []), message]);
      }
      for (const [path, pathMessages] of messages) {
        setError(path, { type: "server", message: pathMessages.join("\n") });
      }
      appliedPaths.current = [...messages.keys()];
      setUnmapped(nextUnmapped);
    },
    [clearErrors, setError]
  );

  return { unmapped, apply };
}
